import * as actionTypes from "./testCasesActionTypes";

const initialState = {
  testCases: [],
  currentTestCase: null,
  loading: false,
  error: null,
  filters: {
    status: null,
    priority: null,
    automation_status: null,
    tags: [],
    created_by: null,
    scenario_id: null,
    search_term: null,
    owner: null,
    state: null,
    review_status: null,
    section_id: null, // Renamed from folder_id
    folder_id: null, // Keep for backward compatibility
    template_id: null,
    filter_logic: "AND",
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  selectedTestCases: [],
  bulkOperationInProgress: false,
};

const testCasesReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch all test cases
    case actionTypes.FETCH_TEST_CASES_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.FETCH_TEST_CASES_SUCCESS:
      return {
        ...state,
        loading: false,
        testCases: action.payload.test_cases || action.payload.data || [],
        pagination: action.payload.pagination || state.pagination,
        error: null,
      };

    case actionTypes.FETCH_TEST_CASES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch single test case
    case actionTypes.FETCH_TEST_CASE_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.FETCH_TEST_CASE_SUCCESS:
      return {
        ...state,
        loading: false,
        currentTestCase:
          action.payload.test_case || action.payload.data || action.payload,
        error: null,
      };

    case actionTypes.FETCH_TEST_CASE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Create test case
    case actionTypes.CREATE_TEST_CASE_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.CREATE_TEST_CASE_SUCCESS:
      const newTestCase =
        action.payload.test_case || action.payload.data || action.payload;
      return {
        ...state,
        loading: false,
        testCases: [newTestCase, ...state.testCases],
        error: null,
      };

    case actionTypes.CREATE_TEST_CASE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Update test case
    case actionTypes.UPDATE_TEST_CASE_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.UPDATE_TEST_CASE_SUCCESS:
      const updatedTestCase =
        action.payload.test_case || action.payload.data || action.payload;
      return {
        ...state,
        loading: false,
        testCases: state.testCases.map((tc) =>
          tc.test_case_id === updatedTestCase.test_case_id
            ? updatedTestCase
            : tc,
        ),
        currentTestCase:
          state.currentTestCase?.test_case_id === updatedTestCase.test_case_id
            ? updatedTestCase
            : state.currentTestCase,
        error: null,
      };

    case actionTypes.UPDATE_TEST_CASE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Delete test case
    case actionTypes.DELETE_TEST_CASE_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.DELETE_TEST_CASE_SUCCESS:
      return {
        ...state,
        loading: false,
        testCases: state.testCases.filter(
          (tc) => tc.test_case_id !== action.payload,
        ),
        currentTestCase:
          state.currentTestCase?.test_case_id === action.payload
            ? null
            : state.currentTestCase,
        error: null,
      };

    case actionTypes.DELETE_TEST_CASE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Copy test case
    case actionTypes.COPY_TEST_CASE_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.COPY_TEST_CASE_SUCCESS:
      const copiedTestCase =
        action.payload.test_case || action.payload.data || action.payload;
      return {
        ...state,
        loading: false,
        testCases: [copiedTestCase, ...state.testCases],
        error: null,
      };

    case actionTypes.COPY_TEST_CASE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Move test case
    case actionTypes.MOVE_TEST_CASE_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.MOVE_TEST_CASE_SUCCESS:
      const movedData = action.payload;
      return {
        ...state,
        loading: false,
        testCases: state.testCases.map((tc) =>
          tc.test_case_id === movedData.testCaseId
            ? {
                ...tc,
                project_id: movedData.targetProjectId,
                section_id:
                  movedData.targetSectionId || movedData.targetFolderId,
                folder_id:
                  movedData.targetSectionId || movedData.targetFolderId,
              }
            : tc,
        ),
        error: null,
      };

    case actionTypes.MOVE_TEST_CASE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Bulk edit test cases
    case actionTypes.BULK_EDIT_TEST_CASES_START:
      return {
        ...state,
        bulkOperationInProgress: true,
        error: null,
      };

    case actionTypes.BULK_EDIT_TEST_CASES_SUCCESS:
      const bulkEdited = action.payload.test_cases || action.payload.data || [];
      const editedIds = bulkEdited.map((tc) => tc.test_case_id);
      return {
        ...state,
        bulkOperationInProgress: false,
        testCases: state.testCases.map((tc) =>
          editedIds.includes(tc.test_case_id)
            ? bulkEdited.find((et) => et.test_case_id === tc.test_case_id) || tc
            : tc,
        ),
        selectedTestCases: [],
        error: null,
      };

    case actionTypes.BULK_EDIT_TEST_CASES_FAILURE:
      return {
        ...state,
        bulkOperationInProgress: false,
        error: action.payload,
      };

    // Bulk delete test cases
    case actionTypes.BULK_DELETE_TEST_CASES_START:
      return {
        ...state,
        bulkOperationInProgress: true,
        error: null,
      };

    case actionTypes.BULK_DELETE_TEST_CASES_SUCCESS:
      return {
        ...state,
        bulkOperationInProgress: false,
        testCases: state.testCases.filter(
          (tc) => !action.payload.includes(tc.test_case_id),
        ),
        selectedTestCases: [],
        error: null,
      };

    case actionTypes.BULK_DELETE_TEST_CASES_FAILURE:
      return {
        ...state,
        bulkOperationInProgress: false,
        error: action.payload,
      };

    // Bulk move test cases
    case actionTypes.BULK_MOVE_TEST_CASES_START:
      return {
        ...state,
        bulkOperationInProgress: true,
        error: null,
      };

    case actionTypes.BULK_MOVE_TEST_CASES_SUCCESS:
      const bulkMoved = action.payload;
      return {
        ...state,
        bulkOperationInProgress: false,
        testCases: state.testCases.map((tc) =>
          bulkMoved.testCaseIds.includes(tc.test_case_id)
            ? {
                ...tc,
                project_id: bulkMoved.targetProjectId,
                section_id:
                  bulkMoved.targetSectionId || bulkMoved.targetFolderId,
                folder_id:
                  bulkMoved.targetSectionId || bulkMoved.targetFolderId,
              }
            : tc,
        ),
        selectedTestCases: [],
        error: null,
      };

    case actionTypes.BULK_MOVE_TEST_CASES_FAILURE:
      return {
        ...state,
        bulkOperationInProgress: false,
        error: action.payload,
      };

    // Set filters
    case actionTypes.SET_TEST_CASES_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear filters
    case actionTypes.CLEAR_TEST_CASES_FILTERS:
      return {
        ...state,
        filters: initialState.filters,
      };

    // Set selected test cases
    case actionTypes.SET_SELECTED_TEST_CASES:
      return {
        ...state,
        selectedTestCases: action.payload,
      };

    // Clear selected test cases
    case actionTypes.CLEAR_SELECTED_TEST_CASES:
      return {
        ...state,
        selectedTestCases: [],
      };

    default:
      return state;
  }
};

export default testCasesReducer;
